import type { MemberRewardsOverview } from '@yoyoclub/shared';
import { Copy, Gift, Loader2, Mail, Send } from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../hooks/useAuth';
import { dateLocale } from '../../i18n/locale';
import { MemberRewardsApiError, memberRewardsApi } from '../../services/memberRewardsApi';
import { withMemberCode } from '../../utils/memberCode';

/**
 * My Home > Rewards (docs/prd-member-rewards.md §4): the member's own promo
 * code, two ways to share it, the rules, and how each friend's order stands.
 */

const RULE_KEYS = ['r1', 'r2', 'r3', 'r4', 'r5'] as const;
const INVITE_ERRORS = ['INVALID_EMAIL', 'OWN_EMAIL', 'DAILY_LIMIT', 'RATE_LIMITED', 'CODE_PAUSED'];

function dollars(cents: number): string {
  return (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function MyRewardsTab() {
  const { t, i18n } = useTranslation('rewards');
  const { isAuthenticated } = useAuth();
  const locale = dateLocale(i18n.language);

  const [data, setData] = useState<MemberRewardsOverview | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [inviteFeedback, setInviteFeedback] = useState<{
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      setData(await memberRewardsApi.overview());
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) void load();
  }, [isAuthenticated, load]);

  const generate = async () => {
    setGenerating(true);
    setGenerateError(false);
    try {
      const created = await memberRewardsApi.createCode();
      setData((prev) =>
        prev ? { ...prev, code: created.code, codeActive: created.codeActive } : prev
      );
    } catch {
      setGenerateError(true);
    } finally {
      setGenerating(false);
    }
  };

  const flash = (kind: 'ok' | 'error', text: string) => {
    setNotice({ kind, text });
    window.setTimeout(() => setNotice(null), 4000);
  };

  const sendInvite = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || sending) return;
    setSending(true);
    setInviteFeedback(null);
    try {
      await memberRewardsApi.invite(email.trim());
      setEmail('');
      setInviteFeedback({ kind: 'ok', text: t('share.sent') });
    } catch (err) {
      const code = err instanceof MemberRewardsApiError ? err.code : '';
      setInviteFeedback({
        kind: 'error',
        text: INVITE_ERRORS.includes(code) ? t(`share.errors.${code}`) : t('share.errors.default'),
      });
    } finally {
      setSending(false);
    }
  };

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p>{t('loadError')}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
      </div>
    );
  }

  const code = data.code;
  const shareable = Boolean(code && data.codeActive);
  const inviteText = code
    ? t('share.inviteText', {
        code,
        url: `${window.location.origin}${withMemberCode('/activities', code)}`,
      })
    : '';

  return (
    <div className="space-y-6">
      <p className="text-gray-600">{t('intro')}</p>

      {/* Your promo code */}
      <section className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('code.label')}</h2>
        {!code ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">{t('code.generateHint')}</p>
            <button
              type="button"
              onClick={generate}
              disabled={generating}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-white font-semibold hover:bg-primary-dark disabled:opacity-60 transition-colors"
            >
              {generating ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Gift className="h-4 w-4" aria-hidden="true" />
              )}
              {generating ? t('code.generating') : t('code.generate')}
            </button>
            {generateError && <p className="text-sm text-red-600">{t('code.generateError')}</p>}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <span
                className={`font-mono text-2xl font-bold tracking-wider ${
                  data.codeActive ? 'text-gray-900' : 'text-gray-400 line-through'
                }`}
              >
                {code}
              </span>
              {data.codeActive && (
                <button
                  type="button"
                  onClick={async () =>
                    (await copyText(code))
                      ? flash('ok', t('code.copied'))
                      : flash('error', t('share.copyFailed'))
                  }
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:border-primary hover:text-primary transition-colors"
                >
                  <Copy className="h-4 w-4" aria-hidden="true" />
                  {t('code.copy')}
                </button>
              )}
            </div>
            {!data.codeActive && <p className="text-sm text-gray-600">{t('code.paused')}</p>}
          </div>
        )}
        {notice && (
          <p
            role="status"
            className={`mt-3 text-sm ${notice.kind === 'ok' ? 'text-primary' : 'text-red-600'}`}
          >
            {notice.text}
          </p>
        )}
      </section>

      {/* Share with friends */}
      {shareable && code && (
        <section className="bg-white rounded-lg shadow-sm p-6 space-y-5">
          <h2 className="text-lg font-semibold text-gray-900">{t('share.title')}</h2>

          <form onSubmit={sendInvite} className="space-y-2">
            <label
              htmlFor="rewards-invite-email"
              className="block text-sm font-medium text-gray-700"
            >
              {t('share.emailLabel')}
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Mail
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
                  aria-hidden="true"
                />
                <input
                  id="rewards-invite-email"
                  type="email"
                  inputMode="email"
                  autoComplete="off"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setInviteFeedback(null);
                  }}
                  placeholder={t('share.emailPlaceholder')}
                  className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-base focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <button
                type="submit"
                disabled={sending || !email.trim()}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-white font-semibold hover:bg-primary-dark disabled:opacity-60 transition-colors"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Send className="h-4 w-4" aria-hidden="true" />
                )}
                {sending ? t('share.sending') : t('share.send')}
              </button>
            </div>
            {inviteFeedback && (
              <p
                role="status"
                className={`text-sm ${inviteFeedback.kind === 'ok' ? 'text-primary' : 'text-red-600'}`}
              >
                {inviteFeedback.text}
              </p>
            )}
          </form>

          <div className="space-y-2">
            <p className="rounded-lg bg-warm-white p-4 text-sm text-gray-700 whitespace-pre-line break-words">
              {inviteText}
            </p>
            <button
              type="button"
              onClick={async () =>
                (await copyText(inviteText))
                  ? flash('ok', t('share.textCopied'))
                  : flash('error', t('share.copyFailed'))
              }
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:border-primary hover:text-primary transition-colors"
            >
              <Copy className="h-4 w-4" aria-hidden="true" />
              {t('share.copyText')}
            </button>
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('rules.title')}</h2>
        <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-700">
          {RULE_KEYS.map((key) => (
            <li key={key}>{t(`rules.${key}`)}</li>
          ))}
        </ol>
      </section>

      {/* My invites */}
      <section className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('invites.title')}</h2>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="rounded-lg bg-yellow-pale p-4">
            <p className="text-sm text-brown">{t('invites.totalRewarded')}</p>
            <p className="text-2xl font-bold text-brown-dark">${dollars(data.totalRewarded)}</p>
          </div>
          <div className="rounded-lg bg-primary-pale p-4">
            <p className="text-sm text-primary-dark">{t('invites.inProgress')}</p>
            <p className="text-2xl font-bold text-primary-dark">{data.inProgressCount}</p>
          </div>
        </div>
        {data.referrals.length === 0 ? (
          <p className="text-sm text-gray-500">{t('invites.empty')}</p>
        ) : (
          <ul className="space-y-2">
            {data.referrals.map((r) => (
              <li
                key={r.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-warm-white px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{r.friendEmailMasked}</p>
                  <p className="text-xs text-gray-500">
                    {t('invites.usedOn', {
                      date: new Date(r.usedAt).toLocaleDateString(locale),
                    })}
                    {r.rewardedAt &&
                      ` · ${t('invites.rewardedOn', {
                        date: new Date(r.rewardedAt).toLocaleDateString(locale),
                      })}`}
                  </p>
                </div>
                <span
                  className={`text-sm font-semibold px-2.5 py-1 rounded-full ${
                    r.status === 'REWARDED'
                      ? 'bg-primary-pale text-primary-dark'
                      : r.status === 'IN_PROGRESS'
                        ? 'bg-yellow-pale text-brown-dark'
                        : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {t(`invites.status.${r.status}`, { amount: dollars(r.rewardAmount ?? 0) })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
